// External Imports
import { Fragment, useContext, useEffect, useState } from "react";
import Heading from "../UI/Heading";
import { UtilityContext } from "../../context/util-context";
import { requestAdminAPI } from "../../api/posh-api";
import { MdRefresh } from "react-icons/md";
import DetailPair from "../UI/DetailPair";
import LoadingCard from "../UI/LoadingCard";
import { useSearchParams } from "react-router-dom";
import NoRecordsFound from "../UI/NoRecordsFound";
import { isoDateToString } from "../../util/format-date";
import { toTitleCase } from "../../util/format-text";
import PersonCard from "../UI/PersonCard";
import AuditLogs from "../UI/AuditLogs";

// Internal Imports

/** ========================================================================
 * * AnnouncementInfo Component
 * This component renders a page display a announcement record.
 * ========================================================================== */

export default function AnnouncementInfo() {
  // Load Context
  const { navigate, setConfirm } = useContext(UtilityContext);

  // Get ID from URL
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  // Initialize States
  const [loading, setLoading] = useState(true);
  const [announcement, setAnnouncement] = useState({});

  /* ====================== FETCH RECORD ====================== */
  const fetchRecord = async () => {
    try {
      setLoading(true);
      const response = await requestAdminAPI("fetch", {
        table: "announcement",
        id: id,
        columns: ["createdBy { name photo email id }"],
      });
      setAnnouncement(response);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ====================== CLICK HANDLER ====================== */
  const clickHandler = (idx) => {
    if (!announcement.recipientType) return;
    const recipientId = announcement.recipientIds[idx];
    if (!recipientId) return;
    navigate(`/${announcement.recipientType}s/info?id=${recipientId}`);
  };

  useEffect(() => {
    fetchRecord();
  }, []);

  return (
    <div className="container">
      <Heading title="Announcement Information" id={id}>
        <AuditLogs table="announcement" id={id} />
        <MdRefresh onClick={fetchRecord} className="icon" />
      </Heading>

      <div className="mb-6 info">
        <DetailPair
          label="Date Created"
          value={isoDateToString(announcement.dateCreated)}
          loading={loading}
        />
        <DetailPair
          label="Recipient Type"
          value={`${toTitleCase(announcement.recipientType)}s`}
          loading={loading}
        />
        <DetailPair
          label="Email Recipients"
          value={announcement.recipientEmails?.length || 0}
          loading={loading}
        />
        <DetailPair
          label="SMS Recipients"
          value={announcement.recipientPhones?.length || 0}
          loading={loading}
        />
        <div className="lg:col-span-2">
          <DetailPair
            label="Announcement"
            value={announcement.message}
            loading={loading}
          />
        </div>
        <div className="lg:col-span-2">
          <DetailPair
            label="Announcement Description"
            value={announcement.description}
            loading={loading}
          />
        </div>
      </div>
      <Heading title="Recipients" isBackArrow={false} text="Click on recipient ID to view recipient information."/>
      {loading ? (
        <LoadingCard columns={5} max={6} />
      ) : announcement.recipientNames.length > 0 ? (
        <Fragment>
          <div className="info">
            {["Name", "Email", "Phone", "ID"].map((header) => (
              <p key={header} className="pl-2 font-semibold text-stone-200">
                {header}
              </p>
            ))}
          </div>
          <div className="list max-h-[600px] gap-2">
            {announcement.recipientNames.map((name, idx) => (
              <div className="info" key={idx} onClick={() => clickHandler(idx)}>
                <p className="cursor-auto form-input-disabled">{name}</p>
                <p className="cursor-auto form-input-disabled">
                  {announcement.recipientEmails[idx] || "N/A"}
                </p>
                <p className="cursor-auto form-input-disabled">
                  {announcement.recipientPhones[idx] || "N/A"}
                </p>
                <p
                  className="cursor-pointer form-input-disabled clickable"
                  onClick={() => clickHandler(idx)}
                >
                  {announcement.recipientIds[idx] || "N/A"}
                </p>
              </div>
            ))}
          </div>
        </Fragment>
      ) : (
        <NoRecordsFound />
      )}

      <div className="min-w-[200px] ml-auto mt-6">
        <PersonCard
          name={announcement.createdBy?.name}
          label="Created By"
          secondary={announcement.createdBy?.email}
          image={announcement.createdBy?.photo}
          loading={loading}
          link={`/employees/info?id=${announcement.createdBy?.id}`}
        />
      </div>
    </div>
  );
}
